import React, { useMemo, memo, useCallback } from 'react';
import { ClipButtonTrackingProperties } from 'types/clips';
import { useUserPermissionStatus } from 'selectors/user';
import InformationPremiumPlus from 'components/InformationPremiumPlus';
import { PRODUCT_TUTORIALS } from 'common/constants';
import ClipButton from 'components/CamPlayerKbygRewinds/ClipButton';
import { trackEvent } from '@surfline/web-common';
import styles from './ClipButtonWrapper.module.scss';

interface Props {
  clipButtonSegmentProperties: ClipButtonTrackingProperties;
  disabled?: boolean;
  disablePaywalls?: boolean;
  href?: string;
  spotId?: string;
  spotName?: string;
  supportsSmartRewinds?: boolean;
}

const ClipButtonWrapper = ({
  clipButtonSegmentProperties,
  disabled,
  disablePaywalls,
  href,
  spotId,
  spotName,
  supportsSmartRewinds,
}: Props) => {
  const { hasCamRewindPermissions } = useUserPermissionStatus();
  const [clipsLoading, setClipsLoading] = React.useState(false);

  const segmentPropsInfoPremiumPlus = useMemo(
    () => ({ location: 'Cam Player Clip Button', spotId, spotName }),
    [spotId, spotName],
  );

  const onClickHandler = useCallback(() => {
    if (!disabled) {
      trackEvent('Clicked Smart Clips', {
        ...clipButtonSegmentProperties,
        clickedCamPlayerPrompt: false,
      });
    }
    setClipsLoading(true);
  }, [disabled, clipButtonSegmentProperties, setClipsLoading]);

  return hasCamRewindPermissions || disablePaywalls ? (
    <ClipButton
      clipsLoading={clipsLoading}
      disabled={disabled}
      href={href}
      onClick={onClickHandler}
      supportsSmartRewinds={supportsSmartRewinds}
      minimizeOnMobile
      btnClassName={styles.clipButton}
    />
  ) : (
    <InformationPremiumPlus
      articleId={PRODUCT_TUTORIALS.articles.paywallSmartClips.id}
      segmentProps={segmentPropsInfoPremiumPlus}
      tooltipText="Upgrade to Premium+ to find your clip"
    >
      <ClipButton
        clipsLoading={clipsLoading}
        disabled={disabled}
        supportsSmartRewinds={supportsSmartRewinds}
        minimizeOnMobile
        btnClassName={styles.clipButton}
        dataTestId="clip-button-paywall"
      />
    </InformationPremiumPlus>
  );
};

export default memo(ClipButtonWrapper);
